/**
 * Classify the one OAuth-redirect-completion failure that is genuinely BENIGN:
 * the browser tore down the Firebase Auth IndexedDB handle while
 * `getRedirectResult()` was still running, because the tab was backgrounded or
 * closing.
 *
 * This exists so the redirect path can tell "nobody is there any more" apart
 * from "a real person just failed to sign in". Those need opposite handling and
 * were previously collapsed into one silent branch:
 *
 *   - teardown  -> warn, say nothing to the user. There is no user; the tab is
 *     going away. Reporting it at error level is the Sentry noise GH #3486 is
 *     about (12 of the 18 observed events).
 *   - everything else -> console.error AND tell the person, because redirect is
 *     the ONLY sign-in path on iOS/iPadOS (see `shouldUseRedirectAuth`) and a
 *     silent failure there returns them to /login with no explanation.
 *
 * WHY A SUBSTRING AND NOT AN EQUALITY CHECK — this is load-bearing, do not
 * "tighten" it to `===`. The message is not stable across SDK versions:
 *
 *   - the events observed in production (OFFICEHUB-CLIENT-2S, -47, 2026-08-05 →
 *     2026-08-20) carry `Database is closing/hidden`;
 *   - the SDK version installed at the time of writing (@firebase/auth 1.13.5)
 *     throws `new Error('Database is closing')` from `_openDb()` — no `/hidden`.
 *
 * Both must classify, so this matches the stable prefix. An exact-match check
 * written against either string alone would silently stop recognising the other,
 * and the failure mode is invisible: the teardown would quietly start
 * console.erroring again (noise returns) rather than breaking anything loudly.
 *
 * Deliberately NOT classified as teardown:
 *   - `auth/web-storage-unsupported` and "missing initial state" — those never
 *     reach this branch; `completePendingRedirect` already swallows them and
 *     returns null (see firebase-auth.ts). Adding them here would be dead code.
 *   - anything carrying a Firebase auth error code — the callers branch on the
 *     user-actionable codes before consulting this.
 */
export function isRedirectStorageTeardownError(error: unknown): boolean {
  const candidate = error as
    | { code?: unknown; message?: unknown }
    | null
    | undefined;

  // A coded error is structurally ineligible to be classified benign.
  //
  // Every user-actionable Firebase auth failure carries a `.code`; the
  // IndexedDB teardown is a bare `Error` and never does. Without this, an
  // error that merely CONTAINS the teardown wording would be downgraded to a
  // warning and shown to nobody — and silence is the one failure direction
  // this whole change exists to remove. Proven rather than assumed: with the
  // message check alone, an `auth/internal-error` whose message reads
  // "Database is closing/hidden" classified as benign (the test below was
  // written first and observed failing on exactly that input).
  //
  // Nothing reachable can populate that message today; this is defense in
  // depth against a future SDK or backend wording change colliding with the
  // substring, where the failure would be invisible — no error, no toast, no
  // Sentry issue, just a user who cannot sign in and is told nothing.
  const code = candidate?.code;
  if (code !== undefined && code !== null && String(code) !== '') return false;

  return /\bdatabase is (closing|hidden)\b/i.test(String(candidate?.message ?? ''));
}
